import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { AlertColor } from "@mui/material";

import { StoreSnackbarQueue } from "@/store";

const DEFAULT_DURATION = 5000; // 5 seconds
const MAX_VISIBLE_SNACKBARS = 1;

/**
 * Individual snackbar notification item
 */
export interface SnackbarItem {
  id: string;
  text: string;
  severity?: AlertColor;
  duration?: number;
  timestamp: number;
}

/**
 * Options for creating a new snackbar
 */
export interface SnackbarOptions {
  severity?: AlertColor;
  duration?: number;
}

/**
 * Hook for managing multiple simultaneous snackbar notifications
 *
 * @example
 * ```tsx
 * const snackbar = useSnackbar();
 *
 * // Show success message
 * snackbar.success("Story saved successfully!");
 *
 * // Show error with custom duration
 * snackbar.error("Failed to load story", { duration: 8000 });
 *
 * // Show custom snackbar
 * snackbar.show("Processing...", { severity: "info" });
 * ```
 *  Success notification
 * <Button
 *   variant="contained"
 *   color="success"
 *   onClick={() => snackbar.success("Story saved successfully!")}
 * >
 *   Show Success
 * </Button>
 *
 *  * Error notification
 * <Button
 *   variant="contained"
 *   color="error"
 *   onClick={() => snackbar.error("Failed to load story")}
 * >
 *   Show Error
 * </Button>
 *
 *  * Warning notification
 * <Button
 *   variant="contained"
 *   color="warning"
 *   onClick={() => snackbar.warning("Your session will expire soon")}
 * >
 *   Show Warning
 * </Button>
 *
 *  * Info notification
 * <Button
 *   variant="contained"
 *   color="info"
 *   onClick={() => snackbar.info("Processing your request...")}
 * >
 *   Show Info
 * </Button>
 *
 *  * <h2>Advanced Usage</h2>
 *
 *  * Custom duration
 * <Button
 *   variant="outlined"
 *   onClick={() =>
 *     snackbar.success("This will stay for 10 seconds", { duration: 10000 })
 *   }
 * >
 *   Custom Duration (10s)
 * </Button>
 *
 *  * No auto-dismiss
 * <Button
 *   variant="outlined"
 *   onClick={() =>
 *     snackbar.error("This won't auto-dismiss", { duration: 0 })
 *   }
 * >
 *   No Auto-Dismiss
 * </Button>
 *
 *  * Multiple simultaneous notifications
 * <Button
 *   variant="outlined"
 *   onClick={() => {
 *     snackbar.info("Starting process...");
 *     setTimeout(() => snackbar.success("Step 1 complete"), 1000);
 *     setTimeout(() => snackbar.success("Step 2 complete"), 2000);
 *     setTimeout(() => snackbar.success("All done!"), 3000);
 *   }}
 * >
 *   Multiple Notifications
 * </Button>
 *
 *  * Manual dismiss
 * <Button
 *   variant="outlined"
 *   onClick={() => {
 *     const id = snackbar.info("Click the X to dismiss", { duration: 0 });
 *     console.log("Snackbar ID:", id);
 *   }}
 * >
 *   Manual Dismiss Only
 * </Button>
 *
 *  * Clear all
 * <Button
 *   variant="outlined"
 *   color="error"
 *   onClick={() => snackbar.hideAll()}
 * >
 *   Clear All Notifications
 * </Button>
 *
 *  * <h2>Real-World Examples</h2>
 *
 *  * Form submission
 * <Button
 *   variant="contained"
 *   onClick={async () => {
 *     snackbar.info("Saving story...");
 *
 *  *     * Simulate API call
 *     setTimeout(() => {
 *       const success = Math.random() > 0.3;
 *       if (success) {
 *         snackbar.success("Story saved successfully!");
 *       } else {
 *         snackbar.error("Failed to save story. Please try again.");
 *       }
 *     }, 1500);
 *   }}
 * >
 *   Simulate Form Submission
 * </Button>
 *
 *  * Batch operations
 * <Button
 *   variant="contained"
 *   onClick={async () => {
 *     const stories = ["Story 1", "Story 2", "Story 3"];

 *     stories.forEach((story, index) => {
 *       setTimeout(() => {
 *         snackbar.success(`Processed ${story}`);
 *       }, index * 1000);
 *     });
 *   }}
 * >
 *   Simulate Batch Processing
 * </Button>
 *
 *
 */
export const useSnackbar = () => {
  const [snackbarQueue, setSnackbarQueue] = useAtom(StoreSnackbarQueue);

  // Track which snackbars have active timers
  const activeTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  /**
   * Generates a unique ID for a snackbar
   */
  const generateId = useCallback((): string => {
    return `snackbar-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }, []);

  /**
   * Adds a new snackbar to the queue
   * Note: Auto-dismiss timer starts only when the snackbar becomes visible
   */
  const show = useCallback(
    (text: string, options?: SnackbarOptions): string => {
      const id = generateId();
      const duration = options?.duration ?? DEFAULT_DURATION;

      const snackbarItem: SnackbarItem = {
        id,
        text,
        severity: options?.severity ?? "info",
        duration,
        timestamp: Date.now(),
      };

      setSnackbarQueue((prev) => [...prev, snackbarItem]);

      return id;
    },
    [generateId, setSnackbarQueue],
  );

  /**
   * Removes a specific snackbar from the queue
   */
  const hide = useCallback(
    (id: string): void => {
      // Clear the timer if it exists
      const timer = activeTimers.current.get(id);
      if (timer) {
        clearTimeout(timer);
        activeTimers.current.delete(id);
      }
      setSnackbarQueue((prev) => prev.filter((item) => item.id !== id));
    },
    [setSnackbarQueue],
  );

  /**
   * Clears all snackbars from the queue
   */
  const hideAll = useCallback((): void => {
    // Clear all active timers
    activeTimers.current.forEach((timer) => clearTimeout(timer));
    activeTimers.current.clear();
    setSnackbarQueue([]);
  }, [setSnackbarQueue]);

  /**
   * Get visible snackbars (max limit) and queued count
   */
  const visibleSnackbars = useMemo(
    () => snackbarQueue.slice(0, MAX_VISIBLE_SNACKBARS),
    [snackbarQueue],
  );

  const queuedCount = useMemo(
    () => Math.max(0, snackbarQueue.length - MAX_VISIBLE_SNACKBARS),
    [snackbarQueue],
  );

  /**
   * Effect: Start auto-dismiss timers for visible snackbars
   * Timers only start when a snackbar becomes visible, ensuring each
   * snackbar is actually displayed for the full duration
   */
  useEffect(() => {
    visibleSnackbars.forEach((snackbar) => {
      // Skip if timer already exists for this snackbar
      if (activeTimers.current.has(snackbar.id)) {
        return;
      }

      // Skip if duration is 0 (manual dismiss only)
      if (snackbar.duration === 0) {
        return;
      }

      // Start auto-dismiss timer
      const timer = setTimeout(() => {
        setSnackbarQueue((prev) =>
          prev.filter((item) => item.id !== snackbar.id),
        );
        activeTimers.current.delete(snackbar.id);
      }, snackbar.duration);

      activeTimers.current.set(snackbar.id, timer);
    });

    // Cleanup: Clear timers for snackbars that are no longer visible
    const visibleIds = new Set(visibleSnackbars.map((s) => s.id));
    activeTimers.current.forEach((timer, id) => {
      if (!visibleIds.has(id)) {
        clearTimeout(timer);
        activeTimers.current.delete(id);
      }
    });
  }, [visibleSnackbars, setSnackbarQueue]);

  /**
   * Cleanup effect: Clear all timers on unmount
   */
  useEffect(() => {
    const timers = activeTimers.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  /**
   * Convenience method for success messages
   */
  const success = useCallback(
    (text: string, options?: Omit<SnackbarOptions, "severity">): string => {
      return show(text, { ...options, severity: "success" });
    },
    [show],
  );

  /**
   * Convenience method for error messages
   */
  const error = useCallback(
    (text: string, options?: Omit<SnackbarOptions, "severity">): string => {
      return show(text, { ...options, severity: "error" });
    },
    [show],
  );

  /**
   * Convenience method for warning messages
   */
  const warning = useCallback(
    (text: string, options?: Omit<SnackbarOptions, "severity">): string => {
      return show(text, { ...options, severity: "warning" });
    },
    [show],
  );

  /**
   * Convenience method for info messages
   */
  const info = useCallback(
    (text: string, options?: Omit<SnackbarOptions, "severity">): string => {
      return show(text, { ...options, severity: "info" });
    },
    [show],
  );

  return useMemo(
    () => ({
      show,
      hide,
      hideAll,
      success,
      error,
      warning,
      info,
      snackbars: visibleSnackbars,
      queuedCount,
      totalCount: snackbarQueue.length,
    }),
    [
      show,
      hide,
      hideAll,
      success,
      error,
      warning,
      info,
      visibleSnackbars,
      queuedCount,
      snackbarQueue.length,
    ],
  );
};
